import {
    IProviderPaymentPageSellStrategyModel
} from "../../../../sell-strategies/provider-payment-page-sell-strategy.model.interface";
import {BookingModel} from "../../../../../booking.model";
import {INeatProviderInfoRawData} from "../../raw-data/neat-raw-data.interfaces";
import {makeObservable, observable, runInAction} from "mobx";
import {NullableString} from "../../../../../../../../types/nullable-types";
import React from "react";
import {InsuranceSelectionAgreementEnum} from "../../../insurance-selection-agreement.enum";
import {BookingSessionStorageKeys} from "../../../../../storage/booking-session-storage-keys";
import {scrollElementIntoViewSmooth} from "../../../../../../../../utils/scroll-element-into-view";
import {INeatPaymentPageSellStrategyViewModel} from "./neat-payment-page-sell-strategy.view-model.interface";
import {NeatInsuranceProductModel} from "../../product/neat-insurance-product.model";
import {NeatSellStrategyBaseModel} from "./neat-sell-strategy-base.model";
import {
    NeatInsuranceSellInPaymentPageComponent
} from "../../../../../../../../components/insurance/neat/neat-insurance-sell-in-payment-page.component";
import {InsuranceSellContextEnum} from "../../../../insurance-sell-context.enum";

export class NeatPaymentPageSellStrategy extends NeatSellStrategyBaseModel implements IProviderPaymentPageSellStrategyModel, INeatPaymentPageSellStrategyViewModel {
    constructor(providerCode: string,
                booking: BookingModel,
                products: NeatInsuranceProductModel[],
                providerInfoJson: INeatProviderInfoRawData,
                customerSessionId: string) {
        super(providerCode, booking, products, providerInfoJson, customerSessionId);

        this._userAgreement = (this._getStorageItem(BookingSessionStorageKeys.insuranceUserAgreement) as InsuranceSelectionAgreementEnum)
            ?? InsuranceSelectionAgreementEnum.None;

        makeObservable<this, '_userAgreement' | '_isValidationActivated' >(this, {
            _userAgreement: observable.ref,
            _isValidationActivated: observable.ref,
        })
    }

    protected _getSellContext(): InsuranceSellContextEnum {
        return InsuranceSellContextEnum.PaymentPage;
    }


    private _userAgreement: InsuranceSelectionAgreementEnum = InsuranceSelectionAgreementEnum.None;
    get userAgreement(): InsuranceSelectionAgreementEnum {
        return this._userAgreement;
    }

    set userAgreement(value: InsuranceSelectionAgreementEnum) {
        runInAction(() => {
            this._userAgreement = value;
        });

        this._setStorageItem(BookingSessionStorageKeys.insuranceUserAgreement, value);
    }

    get product(): NeatInsuranceProductModel {
        return this.availableProducts[0];
    }

    get paymentPageTitle(): NullableString {
        return this.services.language.translate('Flexible cover for your flight');
    }

    renderComponent(): React.ReactElement {
        return <NeatInsuranceSellInPaymentPageComponent neatStrategy={this}/>;
    }

    protected _getSelectedProduct(): NeatInsuranceProductModel | null {
        if(this.userAgreement !== InsuranceSelectionAgreementEnum.Agree) {
            return null;
        }

        return this.product;
    }


    async resetSelection(): Promise<void> {
        const selectedProduct = this._getSelectedProduct();
        if(selectedProduct) {
            await this._removeProduct(selectedProduct);
        }

        this.userAgreement = InsuranceSelectionAgreementEnum.None;
        this.isValidationActivated = false;
    }

    private _isActionInProgress = false;
    async acceptProduct(): Promise<void> {
        if(this._isActionInProgress) {
            return;
        }

        if(this.userAgreement === InsuranceSelectionAgreementEnum.Agree) { //is already selected
            return;
        }

        this._isActionInProgress = true;

        try {

            const tempUserAgreement = this.userAgreement;
            this.userAgreement = InsuranceSelectionAgreementEnum.Agree;

            try {
                await this._sellProduct(this.product);
            } catch (err) {
                this.userAgreement = tempUserAgreement;
                throw err;
            }
        } finally {
            this._isActionInProgress = false;
        }
    }

    async declineProduct(): Promise<void> {
        if(this._isActionInProgress) {
            return;
        }

        if(this.userAgreement === InsuranceSelectionAgreementEnum.Disagree) { //already selected disagree
            return;
        }
        const product = this._getSelectedProduct();
        if(!product) {
            this.userAgreement = InsuranceSelectionAgreementEnum.Disagree;
            return;
        }

        this._isActionInProgress = true;

        try {
            this.userAgreement = InsuranceSelectionAgreementEnum.Disagree;
            await this._removeProduct(product);
        } finally {
            this._isActionInProgress = false;
        }

    }



    private _isValidationActivated: boolean = false;

    get isValidationActivated(): boolean {
        return this._isValidationActivated;
    }

    set isValidationActivated(value: boolean) {
        runInAction(() => {
            this._isValidationActivated = value;
        });
    }

    get errorMessage(): NullableString {

        if(!this.isValidationActivated) {
            return null;
        }

        if(this.userAgreement === InsuranceSelectionAgreementEnum.None) {
            return this.services.language.translate('Please make a choice for your travel insurance');
        }

        return null;
    }

    validate(): NullableString {
        this.isValidationActivated = true;

        const errMsg = this.errorMessage;

        if(errMsg) {
            scrollElementIntoViewSmooth(this._domElement);
        }

        return errMsg;
    }

    private _domElement: HTMLElement | null = null;
    attachValidatedDOMElement(element: HTMLElement | null): void {
        this._domElement = element;
    }


}